import { createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl, headersDefault } from ".";

import { clearCalendarCache } from "../../services/calendarCache";
import { invalidateTrialInfoCache } from "../../services/trialInfoCache";
import { fetchJsonRead } from "../../services/readRequest";
import { clearAllAuthData } from "../../services/authGuard";
import { fetchAppPermissions } from "./permissionsActions";
import { markPremiumPermissionsOptimistic } from "../reducer/permissionsSlice";
import { planRank } from "../../utils/memberPlan";

import { readAuth, writeAuth } from "../../services/authStorage";
// ── Request dedup + throttle for getUserInfo ────────────────────────────
// Mirrors the pattern in courseActions.js: inflight dedup + condition-based
// throttle. When condition returns false, NO actions are dispatched.
let userInfoInflight = null;
let userInfoLastSuccess = 0;
const USER_INFO_THROTTLE_MS = 10_000; // 10 seconds

export const getUserInfo = createAsyncThunk(
  "user/getUserInfo",
  async (_, thunkAPI) => {
    // If a request is already in flight, piggyback on it
    if (userInfoInflight) {
      return userInfoInflight;
    }

    userInfoInflight = (async () => {
      try {
        // Snapshot current subscription state BEFORE the API call
        const prevState = thunkAPI.getState()?.user;
        const prevSubType =
          prevState?.master_club_subscriber?.subscription?.subscription_type
            ?.name ?? null;
        const prevFreeStart =
          prevState?.master_club_subscriber?.subscription?.free_route?.start ??
          null;
        // Premium Lite ↔ Premium keeps the type "premium": the plan changes.
        const prevPlan =
          prevState?.master_club_subscriber?.access_info?.plan ?? null;

        const data = await fetchJsonRead(`${baseUrl}/api/profilo-utenti/1/fetch`, {
          headers: headersDefault(),
        });

        // Always persist the latest profile to localStorage
        writeAuth("adoc_p", JSON.stringify(data));

        // Detect subscription change → invalidate caches
        const newSubType =
          data?.master_club_subscriber?.subscription?.subscription_type?.name ??
          null;
        const newFreeStart =
          data?.master_club_subscriber?.subscription?.free_route?.start ?? null;
        const newPlan = data?.master_club_subscriber?.access_info?.plan ?? null;
        if (
          prevSubType !== newSubType ||
          prevFreeStart !== newFreeStart ||
          (prevPlan !== null && newPlan !== null && prevPlan !== newPlan)
        ) {
          clearCalendarCache();
          invalidateTrialInfoCache();
          // The header PREMIUM label follows this user slice, but the menu /
          // recipe gating lives in the SEPARATE `permissions` slice — which the
          // non-IAP activation paths (web/Stripe purchase, or a CRM-side flip
          // picked up on this refetch) never refreshed, so gates stayed locked
          // until a logout/login. Resync it here so ANY caller that observes the
          // subscription change unlocks the app in place.
          if (String(newSubType || "").toLowerCase() === "premium") {
            // Open the gates instantly and shield them (optimistic TTL) from a
            // lagging "free" reply out of /api/app/permissions/list while the
            // activation webhook is still in flight.
            thunkAPI.dispatch(
              markPremiumPermissionsOptimistic({
                targetPlan: newPlan ?? "premium",
              }),
            );
          }
          // Reconcile with canonical permissions — confirms the upgrade, or
          // re-locks on a downgrade / expiry.
          thunkAPI.dispatch(fetchAppPermissions());
        }

        // Use cached agreements if available; only fetch from API if missing
        let agreements;
        const cachedAgreements = readAuth("adoc_agreements");

        if (cachedAgreements) {
          try {
            const parsed = JSON.parse(cachedAgreements);
            if (Array.isArray(parsed) && parsed.length > 0) {
              agreements = parsed;
            }
          } catch {
            /* invalid cache, will refetch */
          }
        }

        if (!agreements) {
          try {
            const agreementsRes = await fetch(
              `${baseUrl}/api/v1/users/agreements/list`,
              {
                headers: headersDefault(),
              },
            );

            if (agreementsRes.status === 200) {
              const agreementsJson = await agreementsRes.json();
              agreements = Array.isArray(agreementsJson?.data)
                ? agreementsJson.data
                : Array.isArray(agreementsJson)
                ? agreementsJson
                : [];

              localStorage.setItem(
                "adoc_agreements",
                JSON.stringify(agreements),
              );
            }
          } catch (_) {
            // keep existing cache (if any)
          }
        }

        userInfoLastSuccess = Date.now();
        return { ...data, agreements };
      } catch (err) {
        // An invalid/expired bearer token (e.g. a stage token still stored after
        // the app is updated to a prod build, where the boot env-mismatch guard
        // can't help because no adoc_env was recorded) makes the profile fetch
        // 401. Clear all auth so RouteAuth resolves to "none" → pre-login, instead
        // of seeing token-present + no-profile and looping on the onboarding page.
        // 401 only — a 403 means forbidden-for-this-action, not an invalid token.
        if (err?.status === 401) {
          clearAllAuthData();
          // Re-bootstrap from the now-empty storage so RouteAuth lands on
          // pre-login. Guarded to avoid a redirect loop on public pages.
          const path = window?.location?.pathname;
          if (path && path !== "/" && path !== "/login") {
            window.location.href = "/";
          }
        }
        throw err;
      } finally {
        userInfoInflight = null;
      }
    })();

    return userInfoInflight;
  },
  {
    // Return false to silently skip — no pending/fulfilled/rejected dispatched
    condition: (_, { getState }) => {
      const state = getState();
      // If we have data and last success was recent, skip entirely
      if (
        state.user?.name &&
        Date.now() - userInfoLastSuccess < USER_INFO_THROTTLE_MS
      ) {
        return false;
      }
      // If already loading, skip (the inflight dedup handles the actual request)
      if (state.user?.getUserIsLoading && userInfoInflight) {
        return false;
      }
      return true;
    },
  },
);

/** Reset getUserInfo throttle — call after actions that change user/profile state. */
export function resetUserInfoThrottle() {
  userInfoLastSuccess = 0;
  userInfoInflight = null;
}

/**
 * After any activation — a store purchase or a web/Stripe one — the backend can
 * take a moment to register the subscription, so the first profile fetch may
 * still report the pre-purchase status. Poll the profile, bounded and
 * self-terminating, until the backend itself confirms premium.
 *
 * The exit test deliberately asks "is it premium *and real*" rather than "has it
 * changed since we started". A purchase flips the local state to premium
 * optimistically before this runs, so a change-based test compares premium
 * against premium, never matches, and runs every attempt out — reporting nothing
 * useful to the caller either way. `_optimistic` is the marker
 * markPremiumOptimistic leaves behind, and mergeMasterClubSubscriber drops the
 * whole synthesized record the moment a genuinely premium payload arrives.
 *
 * getUserInfo's own change-detection unlocks the permission gates as soon as the
 * change is observed, so no separate permissions call is needed here.
 *
 * Resolves true once the backend has confirmed, false if it never did within the
 * window — callers can use that to tell the user something rather than leaving
 * them on a screen that silently disagrees with what they just paid for.
 */
export const refreshProfileAfterActivation =
  ({ attempts = 8, intervalMs = 2500, targetPlan = null } = {}) =>
  async (dispatch, getState) => {
    const confirmedByBackend = (state) => {
      const subscriber = state?.user?.master_club_subscriber ?? null;
      const subscription = subscriber?.subscription ?? null;
      // Premium Lite → Premium keeps the type: wait for the plan bought.
      const plan = subscriber?.access_info?.plan ?? null;
      const planReached =
        !targetPlan || plan === null || planRank(plan) >= planRank(targetPlan);

      return (
        subscription?.subscription_type?.name === "premium" &&
        subscription?._optimistic !== true &&
        planReached
      );
    };

    for (let i = 0; i < attempts; i++) {
      resetUserInfoThrottle(); // each attempt must hit the network
      await dispatch(getUserInfo());
      if (confirmedByBackend(getState())) return true;
      if (i < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    }

    return confirmedByBackend(getState());
  };

export const postWellnessIndex = createAsyncThunk(
  "user/postWellnessIndex",
  async (feeling) => {
    const response = await fetch(`${baseUrl}/api/master-club-wellness-index`, {
      method: "POST",
      body: JSON.stringify({ index: feeling }),
      headers: headersDefault(),
    });

    if (response.status !== 200)
      throw new Error(response.message ?? "GenericError");

    const data = await response.json();
    return data;
  },
);

export const postNotificationsSubscription = createAsyncThunk(
  "user/postNotificationsSubscription",
  async (subscription) => {
    const response = await fetch(
      `${baseUrl}/api/master-club-push-subscriptions`,
      {
        method: "POST",
        body: JSON.stringify(subscription),
        headers: headersDefault(),
      },
    );

    if (response.status !== 200)
      throw new Error(
        (response.message || (await response.json())) ?? "GenericError",
      );

    return "ok";
  },
);

export const reportProblem = createAsyncThunk(
  "user/reportProblem",
  async ({ message }) => {
    const response = await fetch(
      `${baseUrl}/api/master-club-subscribers/report-problem`,
      {
        body: JSON.stringify({ message }),
        method: "POST",
        headers: headersDefault(),
      },
    );

    if (response.status !== 200)
      throw new Error(
        (response.message || (await response.json()).error) ?? "GenericError",
      );

    return await response.json();
  },
);

export const importSubscription = createAsyncThunk(
  "user/importSubscription",
  async () => {
    const response = await fetch(
      `${baseUrl}/api/master-club-subscribers/import-subscription`,
      {
        method: "POST",
        headers: headersDefault(),
      },
    );

    if (response.status !== 200)
      throw new Error(
        (response.message || (await response.json()).error) ?? "GenericError",
      );

    return await response.json();
  },
);
